<script setup lang="ts">
import SuggestionDropdown from "@/components/SuggestionDropdown.vue";
import type { Query } from "@/models/Query";
import { mapQueryType } from "@/models/Query";
import { computed, ref, watch } from "vue";

interface Props {
  queries?: Query[];
}

const props = withDefaults(defineProps<Props>(), {
  queries: () => []
});

const searchTerm = ref("");
const showDropdown = ref(false);
const emit = defineEmits(["search", "select-query"]);

// Filter queries based on search term
const filteredQueries = computed(() => {
  if (!searchTerm.value || !props.queries) return [];
  
  const term = searchTerm.value.toLowerCase();
  return props.queries.filter(query => {
    return (
      query.id.toLowerCase().includes(term) ||
      query.neuronId.toLowerCase().includes(term) ||
      query.neuronText.toLowerCase().includes(term) ||
      query.author.toLowerCase().includes(term) ||
      query.componentType.toLowerCase().includes(term)
    );
  }).slice(0, 6); // Limit to 6 results
});

// Auto-search when typing
watch(searchTerm, (newVal) => {
  emit("search", newVal);
  showDropdown.value = newVal.length > 0;
});

// Define performSearch function
const performSearch = () => {
  console.log('Performing search for:', searchTerm.value);
  emit("search", searchTerm.value);
  showDropdown.value = false; // Close dropdown after search
};

// Handle query selection
const selectQuery = (query: Query) => {
  emit("select-query", query);
  searchTerm.value = query.id;
  showDropdown.value = false;
};

// Close dropdown when clicking outside - handled by SuggestionDropdown component
</script>

<template>
  <section class="w-full px-4 md:px-8 lg:px-12 mt-4">
    <div class="container mx-auto">
      <div class="flex flex-col lg:flex-row items-center justify-evenly gap-6">
        <div class="w-full lg:w-1/2 space-y-6">
          <div class="text-center lg:text-left">
            <h2 class="text-xl font-bold text-gray-900 leading-tight">
              Ready to discover?
            </h2>
            <h1
              class="text-3xl md:text-4xl font-semibold mt-3 bg-gradient-to-r from-[#34dc1c] from-25% to-gray-900 bg-clip-text text-transparent"
            >
              Find your query's documentation
            </h1>
          </div>

          <!-- Enhanced Search Input with Dropdown -->
          <div class="relative search-container">
            <input
              v-model="searchTerm"
              type="text"
              placeholder="Search by Query ID, Text or Type,..."
              class="w-full h-16 px-6 rounded-full border-2 border-[#157813] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#34dc1c] text-lg"
            />
            <button
              @click="performSearch"
              class="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-[#157813] text-white rounded-full hover:bg-[#0e5a0c] transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </button>

            <!-- Search Results Dropdown using SuggestionDropdown component -->
            <SuggestionDropdown
              :suggestions="filteredQueries.map(query => ({
                id: query.id,
                type: mapQueryType(query.componentType),
                text: query.neuronText,
                author: query.author,
                runCount: query.extraction || 0,
                date: query.queryStartTime,
                tags: query.tags || [],
                features: query.features || []
              }))"
              :is-visible="showDropdown"
              :is-loading="false"
              :highlighted-index="-1"
              :show-footer="true"
              :search-text="searchTerm"
              @select="(suggestion) => selectQuery(props.queries?.find(q => q.id === suggestion.id)!)"
              @highlight="() => {}"
              @close="() => showDropdown = false"
              @search="() => performSearch()"
              @search-click="() => performSearch()"
            />
          </div>
        </div>

        <!-- Retained Image Section -->
        <div class="w-full lg:w-1/2 flex justify-center">
          <img
            src="@/assets/search.png"
            alt="Search interface"
            class="w-full max-w-sm object-contain"
          />
        </div>
      </div>
    </div>
  </section>
</template>
